import React, { memo } from "react";

import { ShowcaseCard, ShowcaseNotice } from "ui/components/Showcase";
import { getVehicleName } from "ui/modules/fleet/vehicles/pages/VehicleDetailPage/components/VehicleDetailCard/components/VehicleEditCard/components/VehicleWizardIntro/helpers";

import { USED_PRICE_INTRO_SAMPLE } from "../consts";
import { UsedPriceSceneProps } from "../types";

/** Korak "Detalji vozila": starost, kilometraža i tehnički podaci koji određuju cijenu. */
const UsedPriceDetailsScene: React.FC<UsedPriceSceneProps> = ({ formValues, vehicleInfo }) => {
  const registrationYear = formValues.firstRegistrationDate
    ? new Date(formValues.firstRegistrationDate).getFullYear()
    : USED_PRICE_INTRO_SAMPLE.firstRegistrationYear;
  const ageYears = Math.max(new Date().getFullYear() - registrationYear, 0);
  const mileage = (formValues.mileageKm ?? USED_PRICE_INTRO_SAMPLE.mileageKm).toLocaleString("hr-HR");
  const co2 = formValues.co2 ?? USED_PRICE_INTRO_SAMPLE.co2;
  const power = formValues.enginePower ?? USED_PRICE_INTRO_SAMPLE.enginePower;

  const specs = [
    { label: "Prva registracija", value: `${registrationYear}.` },
    { label: "Kilometraža", value: `${mileage} km` },
    { label: "Emisija CO2", value: `${co2} g/km` },
    { label: "Snaga motora", value: `${power} kW` },
  ];

  return (
    <>
      <ShowcaseCard className="left-[0.5rem] top-[-1rem] w-[17rem] h-[18rem]" delay={0}>
        <div className="p-5">
          <div className="text-[0.65rem] font-semibold uppercase tracking-wide text-slate-400">Detalji vozila</div>
          <div className="text-sm font-bold text-slate-800 truncate">{getVehicleName(vehicleInfo)}</div>
          <div className="mt-3 space-y-2">
            {specs.map((spec) => (
              <div key={spec.label} className="flex items-center justify-between rounded-md bg-slate-50 px-3 py-2">
                <span className="text-[0.7rem] text-slate-500">{spec.label}</span>
                <span className="text-[0.7rem] font-semibold text-slate-800">{spec.value}</span>
              </div>
            ))}
          </div>
        </div>
      </ShowcaseCard>

      <ShowcaseCard className="right-[-0.5rem] top-[6rem] w-[14rem]" delay={0.25}>
        <div className="p-5">
          <div className="text-[0.65rem] font-semibold uppercase tracking-wide text-slate-400">Starost vozila</div>
          <div className="mt-2 text-xl font-bold text-slate-800">
            {ageYears} {ageYears === 1 ? "godina" : ageYears >= 2 && ageYears <= 4 ? "godine" : "godina"}
          </div>
          {/* Vrijednost pada s godinama: traka pokazuje koliki je dio nove cijene ostao. */}
          <div className="mt-3 h-1.5 w-full rounded-full bg-slate-100">
            <div className="h-1.5 rounded-full bg-amber-400" style={{ width: `${Math.max(100 - ageYears * 9, 20)}%` }} />
          </div>
          <div className="mt-1 text-[0.6rem] text-slate-400">Pad vrijednosti s godinama</div>
        </div>
      </ShowcaseCard>

      <ShowcaseCard className="left-[-1.5rem] bottom-[-0.5rem] w-[15rem]" delay={0.5}>
        <ShowcaseNotice
          title="Starost i kilometraža"
          subtitle="Najviše utječu na cijenu"
          iconClassName="bg-amber-100"
          icon={
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="#B45309" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 14l4-4M3.34 19a10 10 0 1117.32 0" />
            </svg>
          }
        />
      </ShowcaseCard>
    </>
  );
};

export default memo(UsedPriceDetailsScene);
